import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Analyze, ErrorWarningtraiangle } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { NUMBER } from '../../../constant/Number.constant';
import { useCompany } from '../../../hooks/useCompany';
import { useAllowedFeatures } from '../../../hooks/useFeaturePersonalization';
import { useSurvey } from '../../../hooks/useSurvey';
import {
  useLazyFetchSparkAnalyzeQuery,
  useLazyFetchSparkAnalyzeV3Query,
} from '../../../services/charger.api';
import { Charger } from '../../../stores/types';
import { SparkChatTrigger } from '../../../stores/types/sparkChat.interface';
import {
  convertLocalDateToTimezone,
  formatIso,
  getLastOneDay,
  getUTCOffset,
} from '../../../utils/Date.Util';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Icon,
  Label,
  LabelType,
  MODAL_TYPES,
  useGlobalModalContext,
} from '../../_ui';
import {
  CustomDatePicker,
  DATE_PICKER_TYPE,
  DATE_RANGE,
} from '../../_ui/date-picker/Custom-Date-Picker.component';
import { SparkChatWidget } from '../SparkChatWidget.component';
import { formatSparkResult } from '../utils';
import { SparkLoading } from './SparkLoading.component';
import { SparkResults } from './SparkResults.component';

type SparkAnalyzeProps = {
  isModelView: boolean;
  charger: Charger;
  isResultLoading?: boolean;
  setIsResultLoading?: any;
  setSparkResultData?: any;
  selectedDates?: any;
  isSparkLimitExceed?: boolean;
};

const HOURS_THRESHOLD_FOR_V1_API = 3;

/** `interaction_mode.trigger` reported to Spark AI for this entry point. */
const ANALYZE_TRIGGER: SparkChatTrigger = 'analyze-charger';

/**
 * What the picker holds: the preset that is selected, and the `[start, end]` it resolves
 * to. Annotating the state is what keeps the window handed to the chat widget typed as
 * `Date`s -- the `selectedDates` prop is still `any`, so without this the whole
 * downstream read is unchecked.
 */
interface SparkDateTimeRange {
  selOption: DATE_RANGE;
  selDates: Date[];
}

// eslint-disable-next-line react/display-name
export const SparkAnalyze = memo(
  ({
    isModelView,
    charger,
    isResultLoading,
    setIsResultLoading,
    setSparkResultData,
    selectedDates = {
      selDates: getLastOneDay(),
      selOption: DATE_RANGE.LAST_ONE_DAY,
    },
    isSparkLimitExceed = false,
  }: SparkAnalyzeProps) => {
    const { companyId, companyScope } = useCompany();
    // Asserted at the boundary rather than left as `any`: the `selectedDates` prop is
    // untyped, and this is the one place that shape enters the component.
    const [dateTimeRange, setDateTimeRange] = useState<SparkDateTimeRange>(
      selectedDates as SparkDateTimeRange,
    );
    const { t } = useTranslation();
    const { showModal, hideModal } = useGlobalModalContext();
    const { LAST_ONE_HOUR, LAST_THREE_HOUR, LAST_ONE_DAY, LAST_THREE_DAY, CUSTOM_RANGE } =
      DATE_RANGE;

    const [isRecordCountOverSparkLimit, setIsRecordCountOverSparkLimit] =
      useState(isSparkLimitExceed);

    const [triggerFetchSparkAnalyze] = useLazyFetchSparkAnalyzeQuery();

    const [triggerFetchSparkAnalyzeV3] = useLazyFetchSparkAnalyzeV3Query();

    const { triggerSurveyByEvent } = useSurvey();

    const { allowSparkWidgetAnalyzer } = useAllowedFeatures();

    // useSurvey returns a new triggerSurveyByEvent on every render. Depending on it
    // directly would make onCloseModal -> handleSparkResultModel unstable, and since
    // handleSparkResultModel is called inside a useEffect that opens the modal via
    // showModal, that instability caused an infinite render loop. Keep the latest
    // function in a ref so onCloseModal stays referentially stable.
    const triggerSurveyByEventRef = useRef(triggerSurveyByEvent);
    useEffect(() => {
      triggerSurveyByEventRef.current = triggerSurveyByEvent;
    }, [triggerSurveyByEvent]);

    const onCloseModal = useCallback(() => {
      triggerSurveyByEventRef.current('spark-customer-satisfaction-survey');
      hideModal();
    }, [hideModal]);

    const handleSparkResultModel = useCallback(
      (resultData: any) => {
        showModal(MODAL_TYPES.INFO_MODAL, {
          title: t('spark_result_model_header'),
          width: '540px',
          height: 'max-content',
          onRenderBody: () => {
            return (
              <SparkResults
                charger={charger}
                resultData={resultData}
                dateTimeRange={dateTimeRange}
              />
            );
          },
          onIconClick: onCloseModal,
          shouldCloseOnOverlayClick: false,
        });
      },
      [showModal, t, onCloseModal, charger, dateTimeRange],
    );

    // Drive the result UI from the Analyze action itself (the awaited trigger),
    // not from a useEffect watching the query `data`. RTK Query's structural
    // sharing keeps the same `data` reference when a re-analysis returns an
    // identical result, so a data-watching effect would not re-fire on reopen and
    // the results modal would never replace the loading spinner. Awaiting the
    // trigger fires on every click regardless of reference equality.
    const runSparkAnalysis = async () => {
      setIsRecordCountOverSparkLimit(false);
      const [startTime, endTime] = dateTimeRange.selDates;
      const timeDiffInHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
      const isSparkV3 = timeDiffInHours > HOURS_THRESHOLD_FOR_V1_API;

      if (isModelView) {
        setIsResultLoading(true);
        setSparkResultData(null);
      } else {
        showModal(MODAL_TYPES.SPINNER_MODAL, {
          spinnerComponent: <SparkLoading />,
          width: '400px',
          height: '359px',
        });
      }

      try {
        if (isSparkV3) {
          const result = await triggerFetchSparkAnalyzeV3(
            {
              filters: {
                start_time: convertLocalDateToTimezone(
                  startTime,
                  charger?.location?.timeZone || '',
                ),
                time_delta: timeDiffInHours,
                scope: companyScope,
                companyId: charger?.ownerId || companyId,
                timezone: charger?.location?.timeZone,
              },
              chargerId: charger.id,
            },
            false,
          ).unwrap();

          if (isModelView) {
            setSparkResultData(result);
            setIsResultLoading(false);
          } else {
            handleSparkResultModel(result);
          }
        } else {
          const result = await triggerFetchSparkAnalyze(
            {
              filters: {
                chargerId: charger.id,
                'filter[receivedTimeUtc][gte]': formatIso(startTime),
                'filter[receivedTimeUtc][lte]': formatIso(endTime),
                sort: '-receivedTimeUtc',
                limit: NUMBER.ONE_THOUSAND,
                scope: companyScope,
                companyId: charger?.ownerId || companyId,
                timezone: charger?.location?.timeZone,
              },
              chargerId: charger.id,
            },
            false,
          ).unwrap();

          const formatData = formatSparkResult(result.content);
          setIsRecordCountOverSparkLimit(result?.isRecordCountOverSparkLimit);
          const payload = {
            ...formatData,
            copyToClipboardContent: result.content,
          };
          if (isModelView) {
            setSparkResultData(payload);
            setIsResultLoading(false);
          } else {
            handleSparkResultModel(payload);
          }
        }
      } catch (error) {
        if (isModelView) {
          setIsResultLoading(false);
        } else {
          hideModal();
        }
      }
    };

    const isValidDateRange = useMemo(() => {
      // validate future date and time
      return (
        dateTimeRange.selDates.length === 2 &&
        dateTimeRange.selDates[0] <= new Date() &&
        dateTimeRange.selDates[1] <= new Date()
      );
    }, [dateTimeRange.selDates]);

    return (
      <div className='flex flex-col gap-3'>
        <div className='flex flex-row items-center gap-6'>
          {isModelView && (
            <Label
              text={t('spark_time_range_label')}
              type={LabelType.LABEL_S}
              className='min-w-[80px]'
            />
          )}
          <div className='flex flex-col gap-4' data-html2canvas-ignore='true'>
            <div className='flex gap-2'>
              <CustomDatePicker
                pickerType={DATE_PICKER_TYPE.DATE_TIME_PICKER}
                optionList={[
                  LAST_ONE_HOUR,
                  LAST_THREE_HOUR,
                  LAST_ONE_DAY,
                  LAST_THREE_DAY,
                  CUSTOM_RANGE,
                ]}
                format='MMM d,yyyy'
                onChange={(option: any, range: any) => {
                  setIsRecordCountOverSparkLimit(false);
                  setDateTimeRange({ selOption: option, selDates: range });
                }}
                selectedOption={dateTimeRange.selOption}
                selectedDate={dateTimeRange.selDates}
                showHighligtHeader={false}
                timeZone={charger?.location?.timeZone}
              />
              {allowSparkWidgetAnalyzer ? (
                // The Spark chat widget renders its own button and opens the chat over
                // the window selected here.
                <SparkChatWidget
                  charger={charger}
                  label={t('spark_analyze_btn')}
                  dateTimeRange={dateTimeRange}
                  trigger={ANALYZE_TRIGGER}
                  disabled={!isValidDateRange || isResultLoading}
                />
              ) : (
                <Button
                  label={t('spark_analyze_btn')}
                  labelIconSize={IconSize.SIZE_16x16}
                  type={ButtonType.PRIMARY}
                  icon={isModelView ? null : Analyze}
                  size={ButtonSize.SMALL}
                  onClick={runSparkAnalysis}
                  disabled={!isValidDateRange || isResultLoading}
                />
              )}
            </div>
          </div>
        </div>
        {isValidDateRange ? (
          <Label
            text={t('date_picker_time_zone', {
              timeZone: getUTCOffset(charger?.location?.timeZone || ''),
            })}
            type={LabelType.LABEL_XS_MEDIUM}
            color={ColorType.GREY5}
          />
        ) : (
          <div className='p-2 flex flex-row gap-2 border-2 border-warning bg-warning0 rounded-lg'>
            <Icon
              src={ErrorWarningtraiangle}
              size={IconSize.SIZE_24x24}
              color={ColorType.WARNING}
            />
            <Label
              text={t('spark_future_date_warning')}
              type={LabelType.BODY4}
              color={ColorType.GREY6}
            />
          </div>
        )}
        {isModelView && isRecordCountOverSparkLimit && (
          <div className='p-2 flex flex-row gap-2 border-2 border-warning bg-warning0 rounded-lg'>
            <Icon
              src={ErrorWarningtraiangle}
              size={IconSize.SIZE_24x24}
              color={ColorType.WARNING}
            />
            <div className='flex flex-col gap-0'>
              <Label
                text={t('spark_limit_exceed_warning_title')}
                type={LabelType.BODY4_BOLD}
                color={ColorType.GREY6}
              />
              <Label
                text={t('spark_limit_exceed_warning_content')}
                type={LabelType.BODY4}
                color={ColorType.GREY6}
              />
            </div>
          </div>
        )}
      </div>
    );
  },
);
